import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Card, Dropdown, Form, Pagination } from 'react-bootstrap'
import axios from 'axios'
import Cookies from 'js-cookie'
import { BASE_URL } from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import {
  BsSearch,
  BsEyeFill,
  BsFunnelFill,
  BsThreeDotsVertical} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table'

const ViewBusAssignedStd = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()

  const bus = location.state?.bus || null

  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)

  // Filters (same UX idea as DischargedStudent.js)
  const [showFilterCard, setShowFilterCard] = useState(false)
  const [searchName, setSearchName] = useState('')
  const [searchAdmissionNo, setSearchAdmissionNo] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(50)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  // Fetch courses and classes for display (same as ViewAssignedStudents)
  useEffect(() => {
    const fetchCoursesAndClasses = async () => {
      try {
        const institueId = Cookies.get('institute_id')
        const coursesRes = await axios.get(BASE_URL + '/course/all', {
          headers: { 'institute-Id': Number(institueId) }})
        setCourses(coursesRes.data.data || [])

        const classesRes = await axios.get(BASE_URL + '/class/all', {
          headers: { 'institute-Id': Number(institueId) }})
        setClasses(classesRes.data.data || [])
      } catch (error) {
        console.error('Error fetching courses/classes:', error)
      }
    }
    fetchCoursesAndClasses()
  }, [])

  const hardcodedAssignedStudents = useMemo(
    () => [
      {
        id: 101,
        name: 'Aarav Singh',
        admission_number: 'ADM-1001',
        father_name: 'Mahesh Singh',
        email: 'aarav@example.com',
        parents_number: '9999999999',
        course_id: 1,
        class_id: 1,
        admission_date: '2026-03-01'},
      {
        id: 102,
        name: 'Diya Sharma',
        admission_number: 'ADM-1002',
        father_name: 'Rohit Sharma',
        email: 'diya@example.com',
        parents_number: '8888888888',
        course_id: 1,
        class_id: 2,
        admission_date: '2026-03-02'},
      {
        id: 103,
        name: 'Kabir Verma',
        admission_number: 'ADM-1003',
        father_name: 'Sanjay Verma',
        email: 'kabir@example.com',
        parents_number: '7777777777',
        course_id: 2,
        class_id: 1,
        admission_date: '2026-03-03'},
    ],
    [],
  )

  // Demo-only assigned students list (no backend integration requested)
  useEffect(() => {
    setLoading(true)
    try {
      setStudents(hardcodedAssignedStudents)
    } catch (e) {
      toast.error('Failed to load assigned students')
    } finally {
      setLoading(false)
    }
  }, [hardcodedAssignedStudents])

  // Filter logic (name + admission no)
  const filteredStudents = useMemo(() => {
    let filtered = [...students]
    if (searchName.trim()) {
      filtered = filtered.filter((st) =>
        st.name?.toLowerCase().includes(searchName.trim().toLowerCase()),
      )
    }
    if (searchAdmissionNo.trim()) {
      filtered = filtered.filter((st) =>
        String(st.admission_number || '')
          .toLowerCase()
          .includes(searchAdmissionNo.trim().toLowerCase()),
      )
    }
    return filtered
  }, [students, searchName, searchAdmissionNo])

  // Pagination
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage) || 1
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = filteredStudents.slice(indexOfFirstItem, indexOfLastItem)

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1)
  }, [currentPage, totalPages])

  const getCourseName = (courseId) => {
    const course = courses.find((c) => c.id === courseId)
    return course?.name || 'N/A'
  }

  const getClassName = (classId) => {
    const classItem = classes.find((c) => c.id === classId)
    return classItem?.class_name || 'N/A'
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'})
  }

  return (
    <>
      <style>
        {`
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }
          .table td {
            overflow: visible !important;
            user-select: text;
          }
          .table tbody tr {
            overflow: visible !important;
            cursor: default;
          }
          .table-wrapper {
            overflow-x: auto;
            overflow-y: visible !important;
            position: relative;
          }
          .table-card {
            overflow: visible !important;
          }
          .dropdown-menu {
            z-index: 9999 !important;
            position: absolute !important;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px'}}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px'}}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%'}}
            >
              {/* Left: Title */}
              <div className="d-flex align-items-center gap-3">
                {/* School Logo */}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0}}
                >
                  {!logoError ? (
                    <img
                      src="/tta_logobg.png"
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <div>
                  <h3
                    className="m-0 fw-bold text-start"
                    style={{
                      letterSpacing: '-0.02em',
                      overflow: 'visible',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 'clamp(18px, 2.5vw, 22px)',
                      color: '#223843',
                      lineHeight: '1.2',
                      fontFamily: 'Inter, system-ui, sans-serif'}}
                  >
                    Assigned Students
                  </h3>
                  <p
                    className="m-0"
                    style={{
                      fontSize: '13px',
                      color: '#6b7280',
                      fontFamily: 'Inter, system-ui, sans-serif'}}
                  >
                    Bus: {bus?.busName || 'N/A'} ({bus?.busNumber || 'N/A'})
                  </p>
                </div>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px'}}
        >
          {/* Filter Toggle + Items per page */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4'}}
              >
                <span
                  style={{
                    color: '#6b7280',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                >
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: '140px',
                    cursor: 'pointer',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                >
                  <option value="10">10 per page</option>
                  <option value="20">20 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </Form.Select>
              </div>

              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'}}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
          </div>

          {/* Filter Card */}
          <div
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0'}}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'}}
              >
                <div
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6'}}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'}}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5
                          className="m-0 fw-semibold text-start"
                          style={{
                            color: '#111827',
                            fontSize: '16px',
                            fontFamily: 'Inter, system-ui, sans-serif'}}
                        >
                          Search & Filter Students
                        </h5>
                        <p className="m-0 mt-1" style={{ fontSize: '13px', color: '#6b7280' }}>
                          Filter students by name or admission number
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSearchName('')
                        setSearchAdmissionNo('')
                        setCurrentPage(1)
                      }}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #D1D3D4',
                        color: '#111827',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontWeight: 600,
                        fontSize: '13px'}}
                      title="Reset filters"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label
                        className="fw-semibold mb-2 d-block text-start"
                        style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        Search Student Name
                      </label>
                      <div style={{ position: 'relative' }}>
                        <BsSearch
                          style={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#6b7280',
                            fontSize: '16px',
                            zIndex: 1}}
                        />
                        <Form.Control
                          type="text"
                          value={searchName}
                          onChange={(e) => setSearchName(e.target.value)}
                          placeholder="Enter student name..."
                          style={{
                            paddingLeft: '40px',
                            borderRadius: '8px',
                            border: '1px solid #D1D3D4',
                            fontSize: '14px',
                            height: '40px',
                            backgroundColor: '#ffffff',
                            fontFamily: 'Inter, system-ui, sans-serif'}}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <label
                        className="fw-semibold mb-2 d-block text-start"
                        style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        Search by Admission Number
                      </label>
                      <div style={{ position: 'relative' }}>
                        <BsSearch
                          style={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#6b7280',
                            fontSize: '16px',
                            zIndex: 1}}
                        />
                        <Form.Control
                          type="text"
                          value={searchAdmissionNo}
                          onChange={(e) => setSearchAdmissionNo(e.target.value)}
                          placeholder="Enter admission number..."
                          style={{
                            paddingLeft: '40px',
                            borderRadius: '8px',
                            border: '1px solid #D1D3D4',
                            fontSize: '14px',
                            height: '40px',
                            backgroundColor: '#ffffff',
                            fontFamily: 'Inter, system-ui, sans-serif'}}
                        />
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* Table */}
          <Card
            className="table-card"
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              overflow: 'visible',
              backgroundColor: '#ffffff',
              position: 'relative'}}
          >
            <div className="table-wrapper" style={{ overflowX: 'auto', overflowY: 'visible' }}>
              <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'visible' }}>
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    {[
                      'Admission Number',
                      'Name',
                      'Admission Date',
                      'Course',
                      'Class',
                      'Phone',
                      'Action',
                    ].map((h) => (
                      <Th
                        key={h}
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: h === 'Action' ? 'center' : 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          whiteSpace: 'nowrap'}}
                      >
                        {h}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    <Tr>
                      <Td
                        colSpan="7"
                        className="text-center"
                        style={{
                          padding: '40px',
                          fontSize: '14px',
                          color: '#6b7280',
                          fontFamily: 'Inter, system-ui, sans-serif'}}
                      >
                        Loading students...
                      </Td>
                    </Tr>
                  ) : currentItems.length > 0 ? (
                    currentItems.map((st, index) => (
                      <Tr
                        key={st.id || index}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                          transition: 'background-color 0.2s ease',
                          borderBottom: '1px solid #E5E7EB'}}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                        }}
                      >
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827' }}>
                          {st.admission_number || 'N/A'}
                        </Td>
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827' }}>
                          {st.name || 'N/A'}
                        </Td>
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827' }}>
                          {formatDate(st.admission_date)}
                        </Td>
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827' }}>
                          {getCourseName(st.course_id)}
                        </Td>
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827' }}>
                          {getClassName(st.class_id)}
                        </Td>
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827' }}>
                          {st.parents_number || 'N/A'}
                        </Td>
                        <Td className="text-center" style={{ padding: '16px', border: 'none' }}>
                          {/* Desktop dropdown + mobile icon button like DischargedStudent */}
                          {!isMobile ? (
                            <Dropdown>
                              <Dropdown.Toggle
                                variant="light"
                                id={`dropdown-std-${st.id || index}`}
                                className="dropdown-toggle-no-caret"
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  backgroundColor: 'transparent',
                                  border: '1px solid #D1D3D4',
                                  padding: 0,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease'}}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.borderColor = '#223843'
                                  e.currentTarget.style.color = '#ffffff'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent'
                                  e.currentTarget.style.borderColor = '#D1D3D4'
                                  e.currentTarget.style.color = '#111827'
                                }}
                              >
                                <BsThreeDotsVertical style={{ color: 'inherit' }} />
                              </Dropdown.Toggle>
                              <Dropdown.Menu
                                style={{
                                  borderRadius: '8px',
                                  border: '1px solid #D1D3D4',
                                  padding: '8px',
                                  minWidth: '180px'}}
                              >
                                <Dropdown.Item
                                  onClick={() => navigate(`/student/profile/${st.id}`)}
                                  style={{
                                    fontSize: '13px',
                                    padding: '10px 12px',
                                    color: '#111827',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    transition: 'all 0.2s ease',
                                    borderRadius: '6px',
                                    fontFamily: 'Inter, system-ui, sans-serif'}}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#223843'
                                    e.currentTarget.style.color = '#ffffff'
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent'
                                    e.currentTarget.style.color = '#111827'
                                  }}
                                >
                                  <BsEyeFill size={18} /> See Profile
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          ) : (
                            <button
                              onClick={() => navigate(`/student/profile/${st.id}`)}
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                backgroundColor: '#223843',
                                border: 'none',
                                color: '#ffffff',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'}}
                              title="See Profile"
                            >
                              <BsEyeFill size={16} />
                            </button>
                          )}
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td
                        colSpan="7"
                        className="text-center"
                        style={{
                          padding: '40px',
                          fontSize: '14px',
                          color: '#6b7280',
                          fontFamily: 'Inter, system-ui, sans-serif'}}
                      >
                        No records found
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </div>
          </Card>


          {/* Pagination & Total Records */}


                  <TablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          totalItems={filteredStudents.length}
          itemLabel="students"
        />
        </div>
      </div>
    </>
  )
}

export default ViewBusAssignedStd


